import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { SectionHeader, Pill, EmptyState, StatTile } from "@/components/Brand";
import {
  MapPin,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Trash2,
  ClipboardList,
  Calendar,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function AdminSubmissions() {
  const [wards, setWards] = useState([]);
  const [booths, setBooths] = useState([]);
  const [surveyors, setSurveyors] = useState([]);
  const [filters, setFilters] = useState({
    ward_id: "",
    booth_id: "",
    surveyor_id: "",
    date_from: "",
    date_to: "",
    q: "",
  });
  const [data, setData] = useState({ total: 0, items: [] });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [tplCache, setTplCache] = useState(null);

  useEffect(() => {
    api.get("/admin/wards").then((r) => setWards(r.data));
    api.get("/admin/users?role=surveyor").then((r) => setSurveyors(r.data));
    api.get("/form-template").then((r) => setTplCache(r.data));
  }, []);

  useEffect(() => {
    if (!filters.ward_id) {
      setBooths([]);
      return;
    }
    api.get(`/wards/${filters.ward_id}/booths`).then((r) => setBooths(r.data));
  }, [filters.ward_id]);

  const load = async (newSkip = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      params.append("limit", PAGE_SIZE);
      params.append("skip", newSkip);
      const r = await api.get(`/admin/submissions?${params.toString()}`);
      setData(r.data);
      setSkip(newSkip);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.ward_id, filters.booth_id, filters.surveyor_id, filters.date_from, filters.date_to]);

  const onSearch = (e) => {
    e.preventDefault();
    load(0);
  };

  const resetFilters = () =>
    setFilters({ ward_id: "", booth_id: "", surveyor_id: "", date_from: "", date_to: "", q: "" });

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/admin/submissions/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      toast.success("Submission deleted");
      load(skip);
      if (detail?.submission?.id === id) setDetail(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div>
      <SectionHeader eyebrow="Field data" title="Survey Submissions" />

      <div className="wi-stat-row mb-6">
        <StatTile label="Submissions Found" value={data.total} icon={ClipboardList} />
        <StatTile label="Showing" value={data.items.length} hint={`Page ${currentPage} / ${totalPages}`} />
        <StatTile label="Surveyors" value={surveyors.length} icon={Users} />
        <StatTile label="Wards" value={wards.length} icon={MapPin} />
      </div>

      <form onSubmit={onSearch} className="wi-card p-4 mb-5 grid lg:grid-cols-6 md:grid-cols-3 grid-cols-2 gap-3">
        <select
          value={filters.ward_id}
          onChange={(e) => setFilters({ ...filters, ward_id: e.target.value, booth_id: "" })}
          className="wi-f-input"
          data-testid="sub-filter-ward"
        >
          <option value="">All wards</option>
          {wards.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select
          value={filters.booth_id}
          onChange={(e) => setFilters({ ...filters, booth_id: e.target.value })}
          className="wi-f-input"
          disabled={!filters.ward_id}
          data-testid="sub-filter-booth"
        >
          <option value="">All booths</option>
          {booths.map((b) => <option key={b.id} value={b.id}>{b.booth_number}</option>)}
        </select>
        <select
          value={filters.surveyor_id}
          onChange={(e) => setFilters({ ...filters, surveyor_id: e.target.value })}
          className="wi-f-input"
          data-testid="sub-filter-surveyor"
        >
          <option value="">All surveyors</option>
          {surveyors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input
          type="date"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
          className="wi-f-input"
          data-testid="sub-filter-from"
          placeholder="From"
        />
        <input
          type="date"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
          className="wi-f-input"
          data-testid="sub-filter-to"
          placeholder="To"
        />
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Search…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="wi-f-input pl-8 w-full"
              data-testid="sub-filter-q"
            />
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="px-2 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-md"
            data-testid="sub-filter-reset"
          >
            Reset
          </button>
        </div>
      </form>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : data.items.length === 0 ? (
        <EmptyState
          title="No submissions match your filters"
          hint="Try widening the date range or clearing filters."
        />
      ) : (
        <div className="wi-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Submitted", "Ward", "Booth", "Surveyor", "Location", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 data-label">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`submission-row-${s.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {(s.submitted_at || "").replace("T", " ").slice(0, 19)}
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{s.ward_name}</td>
                  <td className="px-4 py-3 text-slate-700">{s.booth_number}</td>
                  <td className="px-4 py-3 text-slate-700">{s.surveyor_name}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-slate-500">
                    {s.location?.lat
                      ? `${s.location.lat.toFixed(4)}, ${s.location.lng.toFixed(4)}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => openDetail(s.id)}
                      className="p-1.5 hover:bg-blue-50 text-blue-700 rounded mr-1"
                      data-testid={`submission-view-${s.id}`}
                      aria-label="View"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => remove(s.id)}
                      className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                      data-testid={`submission-delete-${s.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 bg-slate-50">
            <div className="text-xs font-mono text-slate-500">
              Showing {skip + 1}–{Math.min(skip + data.items.length, data.total)} of {data.total}
            </div>
            <div className="flex gap-1">
              <button
                disabled={skip === 0}
                onClick={() => load(Math.max(0, skip - PAGE_SIZE))}
                className="px-2 py-1.5 text-xs border border-slate-200 rounded-md disabled:opacity-40 hover:bg-white"
                data-testid="sub-page-prev"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                disabled={skip + PAGE_SIZE >= data.total}
                onClick={() => load(skip + PAGE_SIZE)}
                className="px-2 py-1.5 text-xs border border-slate-200 rounded-md disabled:opacity-40 hover:bg-white"
                data-testid="sub-page-next"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {detail && <DetailModal data={detail} tpl={tplCache} onClose={() => setDetail(null)} onDelete={remove} />}

      <style>{`.wi-f-input{ border:1px solid #cbd5e1; border-radius:6px; padding:0.5rem 0.65rem; font-size:0.85rem; background:#fff; color:#0f172a; }
        .wi-f-input:focus{ outline:2px solid #1d4ed8; outline-offset:2px; border-color:transparent; }
        .wi-f-input:disabled{ background:#f1f5f9; color:#94a3b8; }`}</style>
    </div>
  );
}

function DetailModal({ data, tpl, onClose, onDelete }) {
  const { submission, ward, booth } = data;
  // Build a map of question id → label from the form template
  const labelMap = useMemo(() => {
    const m = {};
    (tpl?.sections || []).forEach((sec) => {
      (sec.questions || []).forEach((q) => {
        m[q.id] = { label: q.label, section: sec.title };
      });
    });
    return m;
  }, [tpl]);

  const grouped = useMemo(() => {
    const out = [];
    (tpl?.sections || []).forEach((sec) => {
      const rows = [];
      (sec.questions || []).forEach((q) => {
        if (submission.answers && q.id in submission.answers) {
          rows.push({ id: q.id, label: q.label, value: submission.answers[q.id] });
        }
      });
      if (rows.length) out.push({ title: sec.title, rows });
    });
    // Stray answers not in template
    const known = new Set((tpl?.sections || []).flatMap((s) => s.questions.map((q) => q.id)));
    const stray = Object.entries(submission.answers || {})
      .filter(([k]) => !known.has(k))
      .map(([id, value]) => ({ id, label: id, value }));
    if (stray.length) out.push({ title: "Other", rows: stray });
    return out;
  }, [tpl, submission]);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/55 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-3xl w-full p-6 shadow-xl my-8" data-testid="submission-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="data-label">{ward?.district || ""} · {ward?.name || submission.ward_name}</div>
            <h3 className="text-xl font-semibold text-slate-900">
              Submission · Booth {submission.booth_number}
            </h3>
            <div className="mt-1 text-xs font-mono text-slate-500">
              {(submission.submitted_at || "").replace("T", " ").slice(0, 19)} · {submission.surveyor_name}
              {submission.location?.lat && (
                <> · {submission.location.lat.toFixed(4)}, {submission.location.lng.toFixed(4)}</>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded" data-testid="submission-detail-close">
            <X size={16} />
          </button>
        </div>

        {booth && (
          <div className="mb-4 p-3 bg-slate-50 rounded-md text-xs text-slate-600 flex items-center gap-2">
            <MapPin size={12} /> {booth.polling_station} · {booth.area}
          </div>
        )}

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-2">
          {grouped.map((g) => (
            <div key={g.title}>
              <div className="data-label mb-2">{g.title}</div>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                {g.rows.map((r) => (
                  <div key={r.id} className="grid grid-cols-12 gap-3 px-3 py-2 text-sm">
                    <div className="col-span-5 text-slate-600">{r.label}</div>
                    <div className="col-span-7 text-slate-900 font-medium break-words">
                      <Value v={r.value} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {grouped.length === 0 && (
            <div className="text-sm text-slate-500">No answers recorded.</div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button
            onClick={() => onDelete(submission.id)}
            data-testid="submission-detail-delete"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200"
          >
            <Trash2 size={13} /> Delete submission
          </button>
          <button onClick={onClose} className="px-4 py-1.5 text-sm rounded-md bg-slate-900 hover:bg-slate-800 text-white">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Value({ v }) {
  if (v == null || v === "") return <span className="text-slate-400">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-slate-400">—</span>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {v.map((x, i) => (
          <span key={i} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
            {typeof x === "object" ? x.name || x.id || JSON.stringify(x) : String(x)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof v === "object") return <span className="font-mono text-xs">{JSON.stringify(v)}</span>;
  return <span>{String(v)}</span>;
}
